import { usePlayerEngine } from "../../../../runtime/provider";
import { usePlayerStoreApi } from "@utils/player/core/usePlayer";
import { useEffect } from "react";
import { Platform } from "react-native";
import { auth } from "@utils/firebase";

/**
 * PUBLIC (unauthenticated) ForYou fetches are WEB-ONLY. Native builds require
 * login, so a pre-auth fetch there is wasted work AND a phantom-stream source
 * (2026-08-03): the anonymous queue's head silently becomes the stream
 * tracker's context behind the login screen, and the post-login queue
 * replacement emits a 0-second "seen" completion for a song the user never
 * saw. Authed fetches keep their exact timing — the auth subscription is
 * still armed at mount, so an app restart fetches the moment firebase
 * restores the persisted session.
 */
const allowPublicFetch = () => Platform.OS === "web";

/**
 * Special value for paginationUserId to represent a public (unauthenticated) user.
 * This distinguishes from:
 * - null: fresh install, no pagination history
 * - userUid: logged-in user with their own pagination
 */
export const PUBLIC_USER_ID = "__public__";

/**
 * Bootstraps ForYou recommendations:
 * - If paginationUserId is null (fresh install): fetch public immediately
 * - If paginationUserId is set (PUBLIC_USER_ID or userUid): wait for auth to confirm
 *
 * Uses paginationUserId (persisted) to correctly determine behavior:
 * - Same user resuming session: fetch with existing cursors
 * - Different user or auth change: reset cursors, fetch fresh
 * - Fresh install: fetch public immediately
 *
 * paginationUserId values:
 * - null: fresh install, no pagination history yet
 * - PUBLIC_USER_ID ("__public__"): public user with their own pagination
 * - userUid: logged-in user with their own pagination
 *
 * Idempotent across multiple mounts.
 */
export function useForYouBootstrap(
  enabled = true,
  authedUid: string | null = null,
): void {
  const playerStore = usePlayerStoreApi();
  const bootstrap = usePlayerEngine().bootstrap;

  useEffect(() => {
    // Keep module-level flag in sync so the auth callback (registered once for
    // the shared subscription) can read the latest gating value. Done in an
    // effect rather than during render to avoid mutating module scope mid-render.
    bootstrap.setEnabled(enabled);
  }, [enabled, bootstrap]);

  // DIFFERENT-USER fetch (login / signup / first run after a store wipe) —
  // driven by React state, NOT the raw auth callback. The callback fires the
  // instant firebase mints the account, before React re-renders with the new
  // auth + onboarding state, so its `isEnabled` check reads the STALE pre-auth
  // value and outraces the onboarding gate (2026-08-04 phantom #2: the
  // signup-instant fetch loaded a queue that onboarding's player reset then
  // cleared, emitting a 0-second 'seen' completion for a song never shown).
  // Here `enabled` is current by construction: during signup it stays false
  // through the setup steps (LoadingScreen owns the welcome-queue fetch and
  // stamps paginationUserId), flipping true only when onboarding completes —
  // and then this effect no-ops if the queue is already owned by this user.
  useEffect(() => {
    if (!enabled || !authedUid) return;
    const { paginationUserId } = playerStore.getState();
    if (paginationUserId === authedUid) return;
    playerStore
      .getState()
      .actions.loadForYouInitial({
        forceReplace: true,
        resetCursors: true,
        userId: authedUid,
      })
      .catch(console.error);
  }, [enabled, authedUid, bootstrap, playerStore]);

  useEffect(() => {
    if (!enabled) return;

    if (bootstrap.enter()) {
      if (bootstrap.claimInitial()) {
        const { paginationUserId } = playerStore.getState();

        if (paginationUserId === null && allowPublicFetch()) {
          // Fresh install (web only) - fetch public recommendations
          // immediately. Will set paginationUserId to PUBLIC_USER_ID.
          // Native waits for the auth callback below.
          playerStore
            .getState()
            .actions.loadForYouInitial({ userId: PUBLIC_USER_ID })
            .catch(console.error);
        }
        // If paginationUserId is set (PUBLIC_USER_ID or userUid), wait for auth callback
      }

      const stop = auth.onAuthStateChanged((user) => {
        // Re-check gating: enabled may have changed (e.g. user signed up
        // and entered onboarding) before this callback fires
        if (!bootstrap.enabled()) return;

        const uid = user?.uid ?? null;
        // No user means every fetch below would be public — web-only.
        if (uid === null && !allowPublicFetch()) return;
        const { paginationUserId } = playerStore.getState();

        // Normalize: treat null uid as PUBLIC_USER_ID for comparison
        const effectiveUid = uid ?? PUBLIC_USER_ID;

        // Compare with persisted paginationUserId
        const isSameUser = effectiveUid === paginationUserId;

        if (isSameUser) {
          // Same user resuming session (app RESTART) - fetch with existing
          // cursors the moment firebase restores the persisted session. This
          // is the latency-critical path (operator constraint) and needs no
          // gating: a persisted paginationUserId means onboarding completed.
          if (paginationUserId !== null) {
            playerStore
              .getState()
              .actions.loadForYouInitial({ userId: effectiveUid })
              .catch(console.error);
          }
          return;
        }

        // Different user:
        //  - uid === null (web logout → public browsing): fetch public here.
        //  - uid !== null (login/signup): handled by the gated effect above —
        //    fetching here outraces the onboarding gate via the stale
        //    enabled flag (2026-08-04 phantom #2).
        if (uid === null) {
          playerStore
            .getState()
            .actions.loadForYouInitial({
              forceReplace: true,
              resetCursors: true,
              userId: effectiveUid,
            })
            .catch(console.error);
        }
      });
      bootstrap.attach(stop);
    }

    return bootstrap.release;
  }, [enabled, bootstrap]);
}
